import styled from '@emotion/styled'

import { XSmallPrimaryButton, XSmallSecondaryButton } from '../../../components/Button'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'

// Use container gap on mobile so omitted sections do not leave behind spacing.
export const InvestmentsCardBody = styled.div`
  ${MOBILE} {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`

// Animate the summary's height so the toggle below moves smoothly between states.
export const Collapsible = styled.div`
  display: grid;
  grid-template-rows: ${p => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 280ms ease;

  > * {
    min-height: 0;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SummaryToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: inherit;
  font-size: 18px;
  line-height: 24px;
  text-align: left;
  cursor: pointer;

  /* Remove SVGWrapper's extra line-box height and align the glyph visually with the label. */
  > div {
    display: flex;
    margin-top: 2px;
  }
`

export const ExpanderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: ${p => (p.$expanded ? '29px' : '36px')};

  ${MOBILE} {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`

export const InvestmentActions = styled.div`
  /* Size both desktop columns to the wider button label. */
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 10px;
  width: fit-content;
  margin: 10px 0 34px auto;

  ${MOBILE} {
    grid-auto-flow: row;
    width: auto;
    margin: 24px 0 42px;

    /* Show the primary action first on mobile without changing the desktop order. */
    & > :last-child {
      order: -1;
    }
  }
`

export const EmptyStateActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;

  ${MOBILE} {
    > button {
      width: 100%;
    }
  }
`

/** Row of header controls (upload, link) kept together at the card's top right. */
export const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`

/** Row shown in place of the investments summary while a statement is being processed. */
export const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  font-size: 16px;
  line-height: 22px;

  > svg {
    flex: 0 0 auto;
  }

  ${MOBILE} {
    /* The design puts the glyph after the wrapped text on narrow screens. */
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`

export const AddInvestmentPill = styled(XSmallPrimaryButton)`
  line-height: 22px;
  white-space: nowrap;
`

export const UpdateBalancesPill = styled(XSmallSecondaryButton)`
  font-weight: 400;
  white-space: nowrap;
`

export const LinkedTimestamp = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  color: ${p => p.theme.lightestGray ?? '#7a8e96'};
  font-size: 12px;
  line-height: 24px;

  /* Match the text line height so the icon stays centered on the first line when the date wraps. */
  & > svg {
    flex: 0 0 auto;
    width: 1em;
    height: 24px;
  }
`
